function normalizeImageCandidate(raw: string | null | undefined): string | null {
  if (!raw) return null;

  const value = raw
    .trim()
    .replace(/^['"<\s]+/, '')
    .replace(/['">\s]+$/, '');

  if (!value) return null;

  const lower = value.toLowerCase();
  if (lower === 'null' || lower === 'undefined' || lower === 'about:blank' || value === '#') {
    return null;
  }

  return value;
}

function toAbsoluteUrl(baseUrl: string, value: string | null): string | null {
  const candidate = normalizeImageCandidate(value);
  if (!candidate) return null;
  try {
    return new URL(candidate, baseUrl).toString();
  } catch {
    return candidate;
  }
}

function extractMeta(content: string, property: string, attr: 'property' | 'name' = 'property'): string | null {
  const regex = new RegExp(
    `<meta[^>]+${attr}=["']${property}["'][^>]+content=["']([^"']+)["'][^>]*>`,
    'i',
  );
  const reverseRegex = new RegExp(
    `<meta[^>]+content=["']([^"']+)["'][^>]+${attr}=["']${property}["'][^>]*>`,
    'i',
  );
  return normalizeImageCandidate(content.match(regex)?.[1] ?? content.match(reverseRegex)?.[1] ?? null);
}

function extractAttr(tag: string, attrName: string): string | null {
  const regex = new RegExp(`${attrName}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');
  const match = tag.match(regex);
  const raw = match?.[2] ?? match?.[3] ?? match?.[4] ?? null;
  return normalizeImageCandidate(raw);
}

function extractFromSrcset(srcset: string | null): string | null {
  if (!srcset) return null;
  const first = srcset
    .split(',')
    .map((part) => part.trim())
    .find(Boolean);
  if (!first) return null;
  const [url] = first.split(/\s+/);
  return normalizeImageCandidate(url);
}

export function extractFirstImageUrlFromArticleContent(
  content: string | null | undefined,
): string | null {
  if (!content || typeof content !== 'string') return null;

  // HTML <img> tags (including lazy-loading variants)
  const imgTagRegex = /<img\b[^>]*>/gi;
  let imgTagMatch: RegExpExecArray | null;
  while ((imgTagMatch = imgTagRegex.exec(content)) !== null) {
    const tag = imgTagMatch[0];
    const candidate =
      extractAttr(tag, 'data-src') ||
      extractAttr(tag, 'data-lazy-src') ||
      extractAttr(tag, 'data-original') ||
      extractAttr(tag, 'src') ||
      extractFromSrcset(extractAttr(tag, 'srcset'));
    if (candidate) return candidate;
  }

  // Markdown images ![alt](url "title")
  const markdownImageRegex = /!\[[^\]]*]\(([^)]+)\)/g;
  let markdownMatch: RegExpExecArray | null;
  while ((markdownMatch = markdownImageRegex.exec(content)) !== null) {
    const token = markdownMatch[1]?.trim();
    if (!token) continue;
    const urlPart = token.startsWith('<')
      ? token.slice(1, token.indexOf('>') > 0 ? token.indexOf('>') : undefined)
      : token.split(/\s+/)[0];
    const candidate = normalizeImageCandidate(urlPart);
    if (candidate) return candidate;
  }

  return null;
}

export function resolveFeedPostCoverImage(post: {
  cover_image?: string | null;
  content?: string | null;
}): string | null {
  return (
    normalizeImageCandidate(post.cover_image ?? null) ||
    extractFirstImageUrlFromArticleContent(post.content ?? null)
  );
}

export async function fetchFeedPostSourceImage(sourceUrl: string | null | undefined): Promise<string | null> {
  const normalizedSourceUrl = normalizeImageCandidate(sourceUrl);
  if (!normalizedSourceUrl) return null;

  try {
    const response = await fetch(normalizedSourceUrl, {
      headers: {
        Accept: 'text/html,application/xhtml+xml',
        'User-Agent': 'TaskingBot/1.0',
      },
      redirect: 'follow',
    });

    const contentType = response.headers.get('content-type') || '';
    if (!response.ok || !contentType.includes('text/html')) {
      return null;
    }

    const html = await response.text();
    return toAbsoluteUrl(
      normalizedSourceUrl,
      extractMeta(html, 'og:image') || extractMeta(html, 'twitter:image', 'name'),
    );
  } catch {
    return null;
  }
}
